import { http, buildPath } from './client';
import type {
  Agent,
  Alert,
  AlertDetail,
  AlertFilters,
  AnchorSummary,
  AttestComplete,
  AttestStart,
  BulkAcknowledge,
  Challenge,
  EnrollmentToken,
  EventDetail,
  EventFilters,
  EventSummary,
  Invite,
  InvitePreview,
  Me,
  Member,
  Meta,
  NewWatchRule,
  Org,
  OrgForUser,
  Overview,
  Page,
  PortfolioOrg,
  ProofPackage,
  ProofVc,
  ResolutionOutcome,
  Role,
  Share,
  SharedProof,
  User,
  VerifyResult,
  WatchRule,
  NotificationEmail,
  PolicyTemplate,
} from './types';

/** Typed calls, one per row of docs/API.md. Paths are relative to /v1. */

const o = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}`;
const seg = encodeURIComponent;

export const metaApi = {
  get: () => http.get<Meta>('/meta'),
};

/** The issuer's DID document; it also publishes the anchor-signing key as #anchor. */
export async function issuerDid(): Promise<{ id: string }> {
  const res = await fetch('/.well-known/did.json', { credentials: 'same-origin', headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`DID document: HTTP ${res.status}`);
  return (await res.json()) as { id: string };
}

export const authApi = {
  challenge: () => http.post<Challenge>('/auth/challenge'),
  verify: (body: { challenge: string; address: string; signature: string }) =>
    http.post<VerifyResult>('/auth/verify', body),
  logout: () => http.post<{ ok: true }>('/auth/logout'),
  me: () => http.get<Me>('/me'),
  portfolio: () => http.get<PortfolioOrg[]>('/me/portfolio'),
  updateMe: (body: { displayName?: string | null; email?: string | null }) =>
    http.patch<{ user: User }>('/me', body),
};

export const orgApi = {
  create: (name: string) => http.post<OrgForUser>('/orgs', { name }),
  get: (orgId: string) => http.get<Org>(o(orgId)),
  rename: (orgId: string, name: string) => http.patch<Org>(o(orgId), { name }),

  members: (orgId: string) => http.get<Member[]>(`${o(orgId)}/members`),
  setRole: (orgId: string, userId: string, role: Role) =>
    http.patch<{ ok: true }>(`${o(orgId)}/members/${seg(userId)}`, { role }),
  removeMember: (orgId: string, userId: string) => http.del<{ ok: true }>(`${o(orgId)}/members/${seg(userId)}`),

  invites: (orgId: string) => http.get<Invite[]>(`${o(orgId)}/invites`),
  createInvite: (orgId: string, body: { email?: string; role: Role }) =>
    http.post<{ invite: Invite; link: string; emailed?: { sent: boolean; error?: string } }>(`${o(orgId)}/invites`, body),
  revokeInvite: (orgId: string, inviteId: string) => http.del<{ ok: true }>(`${o(orgId)}/invites/${seg(inviteId)}`),

  notificationEmails: (orgId: string) =>
    http.get<{ emailEnabled: boolean; items: NotificationEmail[] }>(`${o(orgId)}/notification-emails`),
  addNotificationEmail: (orgId: string, email: string) =>
    http.post<NotificationEmail>(`${o(orgId)}/notification-emails`, { email }),
  removeNotificationEmail: (orgId: string, email: string) =>
    http.del<{ ok: true }>(`${o(orgId)}/notification-emails/${seg(email)}`),

  previewInvite: (token: string) => http.get<InvitePreview>(`/invites/${seg(token)}`),
  acceptInvite: (token: string) => http.post<OrgForUser>(`/invites/${seg(token)}/accept`),
};

export const agentApi = {
  list: (orgId: string) => http.get<Agent[]>(`${o(orgId)}/agents`),
  get: (orgId: string, agentId: string) => http.get<Agent>(`${o(orgId)}/agents/${seg(agentId)}`),
  rename: (orgId: string, agentId: string, label: string) =>
    http.patch<Agent>(`${o(orgId)}/agents/${seg(agentId)}`, { label }),
  revoke: (orgId: string, agentId: string) => http.post<Agent>(`${o(orgId)}/agents/${seg(agentId)}/revoke`),

  enrollmentTokens: (orgId: string) => http.get<EnrollmentToken[]>(`${o(orgId)}/enrollment-tokens`),
  createEnrollmentToken: (orgId: string, body: { label?: string; uses?: number; ttlMinutes?: number }) =>
    http.post<{ token: string; enrollment: EnrollmentToken }>(`${o(orgId)}/enrollment-tokens`, body),
  revokeEnrollmentToken: (orgId: string, id: string) =>
    http.del<{ ok: true }>(`${o(orgId)}/enrollment-tokens/${seg(id)}`),
};

export const policyApi = {
  list: (orgId: string) => http.get<WatchRule[]>(`${o(orgId)}/watch-rules`),
  create: (orgId: string, rule: NewWatchRule) => http.post<WatchRule>(`${o(orgId)}/watch-rules`, rule),
  remove: (orgId: string, ruleId: string) => http.del<{ ok: true }>(`${o(orgId)}/watch-rules/${seg(ruleId)}`),
  templates: () => http.get<PolicyTemplate[]>('/policy-templates'),
  applyTemplate: (orgId: string, template: string, agentId: string | null) =>
    http.post<{ added: WatchRule[]; skipped: string[] }>(`${o(orgId)}/watch-rules/from-template`, { template, agentId }),
};

export const evidenceApi = {
  /** A failed verification is still an answer (409 with the package). */
  proof: (orgId: string, eventId: string) =>
    http.get<ProofPackage>(`${o(orgId)}/events/${seg(eventId)}/proof`, { acceptStatuses: [409] }),
  /** The certihash-proof/1 file (an attachment); the server answers 409 until the event is anchored. */
  portableProofHref: (orgId: string, eventId: string) => `/v1${buildPath(`${o(orgId)}/events/${seg(eventId)}/proof`, { format: 'portable' })}`,
  sharedPortableProofHref: (token: string) => `/v1/shared/${seg(token)}/proof.json`,
  proofVc: (orgId: string, eventId: string) =>
    http.get<ProofVc>(`${o(orgId)}/events/${seg(eventId)}/proof`, { query: { format: 'vc-jwt' } }),

  overview: (orgId: string) => http.get<Overview>(`${o(orgId)}/overview`),

  events: (orgId: string, filters: EventFilters, cursor?: string | null, limit = 50) =>
    http.get<Page<EventSummary>>(`${o(orgId)}/events`, {
      query: { ...filters, cursor: cursor ?? undefined, limit },
    }),
  event: (orgId: string, eventId: string) => http.get<EventDetail>(`${o(orgId)}/events/${seg(eventId)}`),
  eventsCsvHref: (orgId: string, filters: EventFilters) => `/v1${buildPath(`${o(orgId)}/events.csv`, filters)}`,

  alerts: (orgId: string, filters: AlertFilters, cursor?: string | null, limit = 50) =>
    http.get<Page<Alert>>(`${o(orgId)}/alerts`, { query: { ...filters, cursor: cursor ?? undefined, limit } }),
  alert: (orgId: string, alertId: string) => http.get<AlertDetail>(`${o(orgId)}/alerts/${seg(alertId)}`),
  acknowledge: (orgId: string, alertId: string) =>
    http.post<Alert>(`${o(orgId)}/alerts/${seg(alertId)}/acknowledge`),
  resolve: (orgId: string, alertId: string, body: { outcome: Exclude<ResolutionOutcome, 'auto_recovered'>; note?: string }) =>
    http.post<AttestStart>(`${o(orgId)}/alerts/${seg(alertId)}/resolve`, body),
  // Bulk: one call for the whole batch, and for resolve, one signature over it.
  acknowledgeMany: (orgId: string, alertIds: string[]) =>
    http.post<BulkAcknowledge>(`${o(orgId)}/alerts/acknowledge`, { alertIds }),
  resolveMany: (
    orgId: string,
    body: { alertIds: string[]; outcome: Exclude<ResolutionOutcome, 'auto_recovered'>; note?: string },
  ) => http.post<AttestStart>(`${o(orgId)}/alerts/resolve`, body),
  completeAttest: (body: { nonce: string; address: string; signature: string }) =>
    http.post<AttestComplete>('/attest/complete', body),

  anchors: (orgId: string, cursor?: string | null, limit = 50) =>
    http.get<Page<AnchorSummary>>(`${o(orgId)}/anchors`, { query: { cursor: cursor ?? undefined, limit } }),

  createShare: (orgId: string, eventId: string, body: { expiresInDays: number; includePayload: boolean; note?: string }) =>
    http.post<{ share: Share; link: string }>(`${o(orgId)}/events/${seg(eventId)}/shares`, body),
  shares: (orgId: string) => http.get<Share[]>(`${o(orgId)}/shares`),
  revokeShare: (orgId: string, shareId: string) => http.del<{ ok: true }>(`${o(orgId)}/shares/${seg(shareId)}`),
  shared: (token: string) => http.get<SharedProof>(`/shared/${seg(token)}`),
};
